import type { TooltipContentProps } from 'recharts'

import { InfoIcon, Text } from '@buffer-mono/popcorn'

import { ChartColorDot } from './ChartColorDot'
import { formatChartDate, formatChartValue } from './chartFormatting'
import { useChartTooltipEntries } from './useChartTooltipEntries'
import type { ChartTooltipValueEntry } from './useChartTooltipEntries'

import styles from './ChartTooltipView.module.css'

import type { JSX } from 'react'

/** Swatch style for a comparison key: a dashed outline (mirroring a dashed
 *  line) or diagonal stripes (mirroring a hatched bar). */
export type ComparisonSwatch = 'dashed' | 'hatched'

type ChartTooltipViewProps = Pick<
  Partial<TooltipContentProps<number, string>>,
  'active' | 'label'
> & {
  /** Show each series' icon beside its label (channel/metric glyph). */
  showIcons?: boolean
  /** Month-only date labels for monthly-bucketed data. */
  monthly?: boolean
  /**
   * Override the date-header format (primary + comparison groups). Defaults to a
   * day / month label driven by `monthly`. Charts with bucketed data (e.g. week
   * or month buckets) pass a bucket-aware formatter so the header reads as the
   * period the point represents.
   */
  formatDate?: (timestampMs: number) => string
  /**
   * Chooses each comparison key's swatch to match how that series is drawn.
   * Defaults to `dashed` for all (mirrors a dashed comparison line); a chart that
   * draws a comparison series as a hatched bar (e.g. the composed chart's posts)
   * returns `hatched` for it.
   */
  comparisonSwatch?: (entry: ChartTooltipValueEntry) => ComparisonSwatch
}

/**
 * The default chart tooltip. Its data comes from {@link useChartTooltipEntries}
 * — the shared, context-driven source of truth (colour, label, value, and the
 * value / no-data / comparison classification) — and this component owns the
 * layout. A null (computed/missing) point renders a plain **"No data available"**
 * row (CORE-6151). Charts that need a different tooltip *layout* can build their
 * own from the hook; charts that only need different date labels pass `formatDate`.
 * Rendered inside Recharts' `<Tooltip>`, so it lives in the provider's tree.
 */
export function ChartTooltipView({
  active,
  label,
  showIcons = false,
  monthly = false,
  formatDate,
  comparisonSwatch = () => 'dashed',
}: ChartTooltipViewProps): JSX.Element | null {
  const entries = useChartTooltipEntries(active, label, { showIcons })

  if (!entries) return null

  const {
    timestampMs,
    primaryEntries,
    noDataEntries,
    comparisonEntries,
    comparisonOriginalDate,
  } = entries

  const formatHeader =
    formatDate ?? ((ts: number): string => formatChartDate(ts, monthly))
  const primaryDate = formatHeader(timestampMs)
  const comparisonDate = comparisonOriginalDate
    ? formatHeader(new Date(comparisonOriginalDate).getTime())
    : ''

  return (
    <div className={styles.tooltip}>
      {(primaryEntries.length > 0 || noDataEntries.length > 0) && (
        <div className={styles.tooltipGroup}>
          <Text size="sm" color="subtle">
            {primaryDate}
          </Text>
          {primaryEntries.map((entry) => (
            <div key={entry.id} className={styles.tooltipItem}>
              <ChartColorDot color={entry.color} />
              {entry.icon}
              <Text size="sm" className={styles.tooltipItemLabel}>
                {entry.label}
              </Text>
              <Text size="sm" weight="bold" className={styles.tooltipValue}>
                {formatChartValue(entry.value, entry.unit)}
              </Text>
            </div>
          ))}
          {noDataEntries.map((entry) => (
            <div key={entry.id} className={styles.tooltipEntry}>
              <div className={styles.tooltipItem}>
                <ChartColorDot color={entry.color} />
                {entry.icon}
                <Text size="sm" className={styles.tooltipItemLabel}>
                  {entry.label}
                </Text>
                <Text size="sm" color="subtle" className={styles.tooltipValue}>
                  –
                </Text>
              </div>
              <div className={styles.note}>
                <InfoIcon size="xsmall" color="subtle" />
                <Text size="sm" className={styles.noteText}>
                  No data available
                </Text>
              </div>
            </div>
          ))}
        </div>
      )}
      {comparisonEntries.length > 0 && (
        <div className={styles.tooltipGroup}>
          <Text size="sm" color="subtle">
            {comparisonDate}
          </Text>
          {comparisonEntries.map((entry) => (
            <div key={entry.id} className={styles.tooltipItem}>
              <ChartColorDot
                color={entry.color}
                dashed={comparisonSwatch(entry) === 'dashed'}
                hatched={comparisonSwatch(entry) === 'hatched'}
              />
              {entry.icon}
              <Text size="sm" className={styles.tooltipItemLabel}>
                {entry.label}
              </Text>
              <Text size="sm" weight="bold" className={styles.tooltipValue}>
                {formatChartValue(entry.value, entry.unit)}
              </Text>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
